import { TicketService } from '@/lib/services/ticket'
import type { ClientOption } from '@/types/ticket'
import type {
  CombinedLocationSearchResults,
  FreeTextLocationResult,
  NominatimAddress,
  StoreLocationResult,
} from '@/types/location'

type NominatimSearchRow = {
  place_id?: number | string
  display_name?: string
  lat?: string
  lon?: string
  address?: NominatimAddress
}

const NOMINATIM_SEARCH_URL = 'https://nominatim.openstreetmap.org/search'
const NOMINATIM_REVERSE_URL = 'https://nominatim.openstreetmap.org/reverse'
const CACHE_TTL_MS = 5 * 60 * 1000
const MAX_CACHE_ENTRIES = 25
/** Light throttle between cascade retries (Nominatim asks for ~1 req/s; keep UI responsive). */
const CASCADE_RETRY_DELAY_MS = 350
/** Cap cascade length so seed geocoding cannot hang for many empty Nominatim rounds. */
const MAX_CASCADE_ATTEMPTS = 4
/** Per-request timeout so a hung Nominatim call cannot leave dispatch loading forever. */
const NOMINATIM_TIMEOUT_MS = 8000
/** Round reverse-geocode cache keys (~1m) to cut duplicate Nominatim calls while dragging. */
const REVERSE_CACHE_PRECISION = 4

type CacheEntry<T> = {
  value: T
  expiresAt: number
}

const storeCache = new Map<string, CacheEntry<StoreLocationResult[]>>()
const locationCache = new Map<string, CacheEntry<FreeTextLocationResult[]>>()
const reverseCache = new Map<string, CacheEntry<FreeTextLocationResult | null>>()

function normalizeKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ')
}

function getCache<T>(cache: Map<string, CacheEntry<T>>, key: string): T | null {
  const entry = cache.get(key)
  if (!entry) return null
  if (entry.expiresAt < Date.now()) {
    cache.delete(key)
    return null
  }
  return entry.value
}

function setCache<T>(cache: Map<string, CacheEntry<T>>, key: string, value: T) {
  cache.set(key, { value, expiresAt: Date.now() + CACHE_TTL_MS })
  if (cache.size <= MAX_CACHE_ENTRIES) return
  const oldestKey = cache.keys().next().value
  if (oldestKey) cache.delete(oldestKey)
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

function toStoreResult(store: ClientOption): StoreLocationResult {
  return {
    type: 'store',
    id: store.storeId,
    store_name: store.storeName,
    store_code: store.storeCode ?? store.branchCode,
    latitude: store.latitude ?? store.location?.latitude ?? null,
    longitude: store.longitude ?? store.location?.longitude ?? null,
    address: store.branchAddress ?? store.locationAddress ?? store.normalizedAddress ?? store.location?.address ?? null,
    business_unit_name: store.businessUnitName ?? store.businessUnit?.name ?? null,
    store,
  }
}

function addressMunicipality(address?: NominatimAddress): string | null {
  if (!address) return null
  return address.city ?? address.town ?? address.village ?? address.municipality ?? address.county ?? null
}

function addressProvince(address?: NominatimAddress): string | null {
  if (!address) return null
  return address.province ?? address.state ?? address.region ?? null
}

function toLocationResult(row: NominatimSearchRow): FreeTextLocationResult | null {
  const latitude = Number(row.lat)
  const longitude = Number(row.lon)
  const displayName = row.display_name?.trim()
  if (!displayName || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null
  }

  return {
    type: 'location',
    display_name: displayName,
    latitude,
    longitude,
    address: row.address,
    municipality: addressMunicipality(row.address),
    province: addressProvince(row.address),
    country: row.address?.country ?? null,
    place_id: row.place_id,
    source: 'nominatim',
  }
}

function reverseCacheKey(latitude: number, longitude: number): string {
  return `${latitude.toFixed(REVERSE_CACHE_PRECISION)},${longitude.toFixed(REVERSE_CACHE_PRECISION)}`
}

function formatPinnedDisplayName(latitude: number, longitude: number): string {
  return `Pinned location (${latitude.toFixed(5)}, ${longitude.toFixed(5)})`
}

function mentionsPhilippines(query: string): boolean {
  return /\bphilippines\b|\bph\b/i.test(query)
}

function withPhilippines(query: string): string {
  const trimmed = query.trim().replace(/\s+/g, ' ')
  if (!trimmed) return trimmed
  if (mentionsPhilippines(trimmed)) return trimmed
  return `${trimmed}, Philippines`
}

function stripUnitFloorPrefix(query: string): string | null {
  const next = query
    .replace(
      /^(unit\s*[#.]?\s*[a-z0-9]+|room\s*[#.]?\s*\d+|rm\.?\s*\d+|[#]\s*\d+|\d+\s*[/-]?\s*f(?:loor)?)\s*[,.]?\s*/i,
      '',
    )
    .trim()
  return next.length >= 3 && normalizeKey(next) !== normalizeKey(query) ? next : null
}

function stripLeadingBrand(query: string): string | null {
  const next = query
    .replace(
      /^(uniqlo|h&m|nike|adidas|zara|starbucks|mcdonald'?s?|mcd|jollibee|kfc|sephora|miniso|penshoppe|bench|burger\s*king|chowking|mang\s*inasal|greenwich|red\s*ribbon|goldilocks|sbarro|pizza\s*hut|dunkin'?|coffee\s*bean)\s+/i,
      '',
    )
    .trim()
  return next.length >= 3 && normalizeKey(next) !== normalizeKey(query) ? next : null
}

/**
 * Build PH-scoped Nominatim query variants for a free-text address.
 * Callers try these in order until Nominatim returns a hit.
 */
export function buildNominatimQueryCascade(keyword: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []

  const push = (raw: string | null | undefined) => {
    if (!raw) return
    const query = withPhilippines(raw)
    const key = normalizeKey(query)
    if (key.length < 3 || seen.has(key)) return
    seen.add(key)
    out.push(query)
  }

  const base = keyword.trim().replace(/\s+/g, ' ')
  if (base.length < 3) return []

  push(base)

  const withoutUnit = stripUnitFloorPrefix(base)
  push(withoutUnit)

  const candidates = [base, withoutUnit].filter((value): value is string => Boolean(value))
  for (const candidate of candidates) {
    const parts = candidate
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)

    if (parts.length >= 2) {
      // Drop leading building/unit clause → street + city
      push(parts.slice(1).join(', '))
      if (parts.length >= 3) {
        push(parts.slice(-2).join(', '))
      }
    }

    push(stripLeadingBrand(candidate))
    if (parts[0]) {
      push(stripLeadingBrand(parts[0]))
    }
  }

  return out
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) return Promise.resolve()
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      window.clearTimeout(timer)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/** Merge caller abort with a hard timeout so Nominatim cannot hang indefinitely. */
function withTimeoutSignal(signal: AbortSignal | undefined, timeoutMs: number): {
  signal: AbortSignal
  clear: () => void
} {
  const controller = new AbortController()
  const timer = window.setTimeout(() => {
    controller.abort()
  }, timeoutMs)

  const onAbort = () => {
    window.clearTimeout(timer)
    controller.abort()
  }
  signal?.addEventListener('abort', onAbort, { once: true })

  return {
    signal: controller.signal,
    clear: () => {
      window.clearTimeout(timer)
      signal?.removeEventListener('abort', onAbort)
    },
  }
}

async function fetchNominatimOnce(
  query: string,
  signal?: AbortSignal,
): Promise<FreeTextLocationResult[]> {
  const normalized = normalizeKey(query)
  if (normalized.length < 3) return []

  const cached = getCache(locationCache, normalized)
  if (cached) return cached

  const params = new URLSearchParams({
    q: query.trim(),
    format: 'jsonv2',
    limit: '5',
    addressdetails: '1',
    countrycodes: 'ph',
  })

  const timeout = withTimeoutSignal(signal, NOMINATIM_TIMEOUT_MS)
  try {
    const response = await fetch(`${NOMINATIM_SEARCH_URL}?${params.toString()}`, {
      method: 'GET',
      signal: timeout.signal,
      headers: {
        Accept: 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error(`Nominatim search failed (${response.status})`)
    }
    const rows = (await response.json()) as NominatimSearchRow[]
    const results = Array.isArray(rows)
      ? rows.map(toLocationResult).filter((item): item is FreeTextLocationResult => item != null)
      : []
    setCache(locationCache, normalized, results)
    return results
  } catch (error) {
    if (isAbortError(error)) {
      // Distinguish caller cancel from timeout: rethrow caller abort so cascade stops.
      if (signal?.aborted) throw error
      return []
    }
    throw error
  } finally {
    timeout.clear()
  }
}

export const LocationService = {
  async searchStore(keyword: string, clientCode?: string, signal?: AbortSignal): Promise<StoreLocationResult[]> {
    const normalized = normalizeKey(keyword)
    if (normalized.length < 3) return []

    const cacheKey = `${clientCode ?? ''}:${normalized}`
    const cached = getCache(storeCache, cacheKey)
    if (cached) return cached

    const response = await TicketService.searchStoreOptions(keyword, clientCode, signal)
    if (!response.success || !response.data) return []

    const results = response.data.map(toStoreResult)
    setCache(storeCache, cacheKey, results)
    return results
  },

  /**
   * PH-only Nominatim search with cascade fallbacks for unit/building/brand queries.
   * Shared by LocationSearchInput and ticket-request seed geocoding.
   */
  async searchLocation(keyword: string, signal?: AbortSignal): Promise<FreeTextLocationResult[]> {
    const originalKey = normalizeKey(keyword)
    if (originalKey.length < 3) return []

    const cachedOriginal = getCache(locationCache, originalKey)
    if (cachedOriginal) return cachedOriginal

    const cascade = buildNominatimQueryCascade(keyword).slice(0, MAX_CASCADE_ATTEMPTS)
    if (cascade.length === 0) return []

    try {
      for (let index = 0; index < cascade.length; index++) {
        if (signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError')
        }
        if (index > 0) {
          await delay(CASCADE_RETRY_DELAY_MS, signal)
        }
        const matchedQuery = cascade[index]
        const results = await fetchNominatimOnce(matchedQuery, signal)
        if (results.length > 0) {
          const stamped = results.map((row) => ({
            ...row,
            isApproximate: index > 0,
            matchedQuery,
            cascadeIndex: index,
          }))
          setCache(locationCache, originalKey, stamped)
          return stamped
        }
      }
      setCache(locationCache, originalKey, [])
      return []
    } catch (error) {
      if (isAbortError(error)) {
        // Do not cache empty on abort — caller may retry after remount.
        return []
      }
      throw error
    }
  },

  async searchCombined(
    keyword: string,
    clientCode?: string,
    signal?: AbortSignal,
  ): Promise<CombinedLocationSearchResults> {
    const normalized = normalizeKey(keyword)
    if (normalized.length < 3) {
      return { stores: [], locations: [] }
    }

    const [storeResult, locationResult] = await Promise.allSettled([
      LocationService.searchStore(keyword, clientCode, signal),
      LocationService.searchLocation(keyword, signal),
    ])

    return {
      stores: storeResult.status === 'fulfilled' ? storeResult.value : [],
      locations: locationResult.status === 'fulfilled' ? locationResult.value : [],
    }
  },

  /**
   * Reverse-geocode lat/lng via Nominatim. On miss/timeout returns a manual_pin
   * fallback with a coordinate label so dispatch can still confirm the pin.
   */
  async reverseGeocode(
    latitude: number,
    longitude: number,
    signal?: AbortSignal,
  ): Promise<FreeTextLocationResult | null> {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null

    const cacheKey = reverseCacheKey(latitude, longitude)
    const cached = getCache(reverseCache, cacheKey)
    if (cached !== null) return cached

    const params = new URLSearchParams({
      lat: String(latitude),
      lon: String(longitude),
      format: 'jsonv2',
      addressdetails: '1',
      zoom: '18',
    })

    const timeout = withTimeoutSignal(signal, NOMINATIM_TIMEOUT_MS)
    try {
      const response = await fetch(`${NOMINATIM_REVERSE_URL}?${params.toString()}`, {
        method: 'GET',
        signal: timeout.signal,
        headers: {
          Accept: 'application/json',
        },
      })
      if (!response.ok) {
        throw new Error(`Nominatim reverse failed (${response.status})`)
      }
      const row = (await response.json()) as NominatimSearchRow & { error?: string }
      if (row?.error) {
        const fallback: FreeTextLocationResult = {
          type: 'location',
          display_name: formatPinnedDisplayName(latitude, longitude),
          latitude,
          longitude,
          source: 'manual_pin',
        }
        setCache(reverseCache, cacheKey, fallback)
        return fallback
      }

      const mapped = toLocationResult({
        ...row,
        lat: row.lat ?? String(latitude),
        lon: row.lon ?? String(longitude),
      })
      const result: FreeTextLocationResult = mapped
        ? { ...mapped, latitude, longitude, source: 'manual_pin' }
        : {
            type: 'location',
            display_name: formatPinnedDisplayName(latitude, longitude),
            latitude,
            longitude,
            source: 'manual_pin',
          }
      setCache(reverseCache, cacheKey, result)
      return result
    } catch (error) {
      if (isAbortError(error)) {
        if (signal?.aborted) throw error
        return {
          type: 'location',
          display_name: formatPinnedDisplayName(latitude, longitude),
          latitude,
          longitude,
          source: 'manual_pin',
        }
      }
      return {
        type: 'location',
        display_name: formatPinnedDisplayName(latitude, longitude),
        latitude,
        longitude,
        source: 'manual_pin',
      }
    } finally {
      timeout.clear()
    }
  },
}
